import { FC, memo, useMemo } from 'react';
import { Tooltip, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { TableFooter } from '../../TableFooter';
import { EditOutlined, ArrowUpOutlined } from '@ant-design/icons';
import {
  CallPoolWithClinics,
  EditableRow,
} from '../../../models/Settings/CallPool';
import { Clinic } from '../../../models/MedVox/Clinic';
import { LoadingTable } from '../../shared/Loading';
import { TableResults } from '../../shared/TableResults';
import styles from './styles.module.css';

interface Props {
  items: CallPoolWithClinics[];
  totalItemsCount: number;
  pageIndex: number;
  pageSize: number;
  loading: boolean;
  hasLoadError?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (value: string) => void;
  onStartEdit: (record: EditableRow) => void;
  poolClinicsLoading: Record<string, boolean>;
  actionsDisabled?: boolean;
}

export const CallPoolSettingsTable: FC<Props> = memo(
  ({
    items,
    totalItemsCount,
    pageIndex,
    pageSize,
    loading,
    hasLoadError = false,
    onPageChange,
    onPageSizeChange,
    onStartEdit,
    poolClinicsLoading,
    actionsDisabled = false,
  }: Props) => {
    const titleSortHeader = (
      <span className={styles.titleSortHint}>
        <span className={styles.titleSortLabel}>Наименование (А-Я)</span>
        <ArrowUpOutlined className={styles.titleSortIcon} />
      </span>
    );

    const dataSource = useMemo(() => {
      const data: EditableRow[] = items.map((item) => ({
        key: item.id,
        id: item.id,
        title: item.title,
        channelsCount: item.channelsCount,
        clinics: item.clinics,
      }));
      return data;
    }, [items]);

    const pageChannelsCount = useMemo(() => {
      return dataSource.reduce((total, row) => total + row.channelsCount, 0);
    }, [dataSource]);

    const renderClinics = (clinics: Clinic[]) => {
      if (!clinics || clinics.length === 0) return '—';
      return (
        <span className={styles.clinicsText}>
          {clinics.map((c) => c.name).join(', ')}
        </span>
      );
    };

    const columns: ColumnsType<EditableRow> = [
      {
        title: titleSortHeader,
        dataIndex: 'title',
        key: 'title',
        width: '30%',
        render: (text: string) => text,
      },
      {
        title: 'Каналы',
        dataIndex: 'channelsCount',
        key: 'channelsCount',
        width: 150,
        render: (count: number) => count,
      },
      {
        title: 'Медицинские организации',
        dataIndex: 'clinics',
        key: 'clinics',
        className: styles.clinicsColumn,
        onCell: () => ({
          style: { maxWidth: 0 },
        }),
        render: (clinics: Clinic[], record: EditableRow) => {
          return record.id && poolClinicsLoading[record.id] ? (
            <Spin size="small" />
          ) : (
            renderClinics(clinics)
          );
        },
      },
      {
        key: 'actions',
        width: 80,
        align: 'center',
        render: (_, record: EditableRow) => {
          return (
            <div className={styles.actionsCell}>
              <Tooltip title="Редактировать">
                <button
                  type="button"
                  className={styles.actionBtn}
                  onClick={() => onStartEdit(record)}
                  disabled={actionsDisabled}
                >
                  <EditOutlined className={styles.editIcon} />
                </button>
              </Tooltip>
            </div>
          );
        },
      },
    ];

    return (
      <div className={styles.callPoolSettingsTable}>
        <TableResults
          isEmpty={items.length === 0 && totalItemsCount === 0 && !hasLoadError}
          loading={loading}
        >
          <div className={styles.tableScroll}>
            <LoadingTable<EditableRow>
              className={styles.table}
              columns={columns}
              dataSource={dataSource}
              pagination={false}
              loading={loading}
              rowKey="key"
            />
          </div>

          <TableFooter
            pageIndex={pageIndex}
            pageSize={pageSize}
            total={totalItemsCount}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            centerContent={`Указано каналов (на странице): ${pageChannelsCount}`}
            disabled={loading}
          />
        </TableResults>
      </div>
    );
  },
);

CallPoolSettingsTable.displayName = 'CallPoolSettingsTable';
